import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { getCurrencySymbol } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';

const ExpenseModal = ({ open, onClose, expense, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    amount: 0,
    category: 'Operational',
    date: new Date().toISOString().split('T')[0],
    account_id: '',
    notes: '',
    project_id: ''
  });
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      const [accRes, projRes] = await Promise.all([
        apiClient.get('/accounts'),
        apiClient.get('/projects')
      ]);
      setAccounts(accRes.data);
      setProjects(projRes.data);

      if (!expense && accRes.data.length > 0) {
        const defaultAcc = accRes.data.find(a => a.is_default) || accRes.data[0];
        setFormData(prev => ({ ...prev, account_id: defaultAcc.account_id }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [expense]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (expense) {
      setFormData({
        amount: expense.amount || 0,
        category: expense.category || 'Operational',
        date: expense.date || new Date().toISOString().split('T')[0],
        account_id: expense.account_id || '',
        notes: expense.notes || '',
        project_id: expense.project_id || ''
      });
    } else {
      // Find default account
      const defaultAcc = accounts.find(a => a.is_default);
      setFormData({
        amount: 0,
        category: 'Operational',
        date: new Date().toISOString().split('T')[0],
        account_id: defaultAcc ? defaultAcc.account_id : (accounts[0]?.account_id || ''),
        notes: '',
        project_id: ''
      });
    }
  }, [expense, accounts, open, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.amount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    if (!formData.account_id) {
      toast.error('Please select an account');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (expense) {
        // Expense update not implemented on backend yet but for completeness
        await apiClient.put(`/expenses/${expense.expense_id}`, submitData);
        toast.success('Expense updated successfully');
      } else {
        await apiClient.post('/expenses', submitData);
        toast.success('Expense recorded successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{expense ? 'Edit Expense' : 'Record Expense'}</DialogTitle>
          <DialogDescription>
            Track your business costs and overheads.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount ({getCurrencySymbol(user?.currency)}) *</Label>
              <Input
                id="amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="bg-slate-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-slate-50"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
              <SelectTrigger className="bg-slate-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Operational">Operational</SelectItem>
                <SelectItem value="Marketing">Marketing</SelectItem>
                <SelectItem value="Software/SaaS">Software/SaaS</SelectItem>
                <SelectItem value="Travel">Travel</SelectItem>
                <SelectItem value="Team/Salaries">Team/Salaries</SelectItem>
                <SelectItem value="Taxes">Taxes</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="account">Paid From *</Label>
            <Select value={formData.account_id} onValueChange={(value) => setFormData({ ...formData, account_id: value })}>
              <SelectTrigger className="bg-slate-50">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map(acc => (
                  <SelectItem key={acc.account_id} value={acc.account_id}>
                    {acc.name} ({getCurrencySymbol(user?.currency)}{acc.balance.toLocaleString()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="project">Link to Project (Optional)</Label>
            <Select
                value={formData.project_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, project_id: value === "none" ? "" : value })}
            >
              <SelectTrigger className="bg-slate-50">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {projects.map(proj => (
                  <SelectItem key={proj.project_id} value={proj.project_id}>{proj.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="What was this for?"
              className="bg-slate-50"
              rows={2}
            />
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="bg-rose-600 hover:bg-rose-700 text-white" disabled={loading}>
              {loading ? 'Saving...' : 'Record Expense'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseModal;
